import { alpha, createTheme } from '@mui/material/styles';
import { appleFontStack, textScale } from './standards';

const hoodwisePrimary = '#FF385C';
const hoodwisePrimaryDark = '#C81E42';
const hoodwisePrimaryLight = '#FF6F85';
const hoodwiseSecondary = '#00A699';
const hoodwiseSecondaryDark = '#007866';
const hoodwiseSecondaryLight = '#2ACEBA';
const hoodwiseCharcoal = '#222222';
const hoodwiseMuted = '#717171';
const hoodwiseSurface = '#FFFFFF';
const hoodwiseBackground = '#F7F7F7';
const hoodwiseDivider = '#E0E0E0';
const hoodwiseHighlight = '#FF9500';

export const plannerTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: hoodwisePrimary,
      light: hoodwisePrimaryLight,
      dark: hoodwisePrimaryDark,
      contrastText: hoodwiseSurface,
    },
    secondary: {
      main: hoodwiseSecondary,
      light: hoodwiseSecondaryLight,
      dark: hoodwiseSecondaryDark,
      contrastText: hoodwiseSurface,
    },
    background: {
      default: hoodwiseBackground,
      paper: hoodwiseSurface,
    },
    text: {
      primary: hoodwiseCharcoal,
      secondary: hoodwiseMuted,
    },
    divider: hoodwiseDivider,
    info: {
      main: hoodwiseHighlight,
    },
    action: {
      hover: alpha(hoodwiseCharcoal, 0.08),
      selected: alpha(hoodwisePrimary, 0.18),
      focus: alpha(hoodwisePrimary, 0.24),
      active: hoodwisePrimaryDark,
    },
  },
  typography: {
    fontFamily: appleFontStack,
    h1: textScale.hero,
    h2: textScale.headline,
    h3: textScale.headline,
    h4: {
      ...textScale.headline,
      fontSize: 'clamp(1.1rem, 1.5vw, 1.5rem)',
    },
    h5: { ...textScale.body, fontWeight: 600 },
    h6: textScale.body,
    subtitle1: textScale.body,
    subtitle2: { ...textScale.support, fontWeight: 500 },
    body1: textScale.body,
    body2: textScale.support,
    button: {
      ...textScale.body,
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: textScale.support,
    overline: {
      ...textScale.support,
      fontWeight: 600,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: hoodwiseBackground,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          paddingInline: '1.5rem',
          paddingBlock: '0.65rem',
          fontWeight: 600,
        },
        containedPrimary: ({ theme }) => ({
          boxShadow: '0 14px 32px rgba(255, 56, 92, 0.18)',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 18px 40px rgba(200, 30, 66, 0.22)',
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          borderColor: alpha(theme.palette.primary.main, 0.32),
          color: theme.palette.primary.main,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 32px 60px rgba(34, 34, 34, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(hoodwiseSurface, 0.92),
          color: hoodwiseCharcoal,
          boxShadow: '0 12px 32px rgba(34, 34, 34, 0.08)',
          backdropFilter: 'blur(18px)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: hoodwiseDivider,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: 500,
          color: theme.palette.text.primary,
          backgroundColor: alpha(hoodwiseSurface, 0.9),
          borderRadius: 999,
        }),
      },
    },
  },
});
